import { useEffect } from 'react';
import { isNativeBuild } from '@/lib/config';

/**
 * Routes where pressing back should leave the app rather than navigate.
 * Everything else in /app is reachable only by pushing history.
 */
const APP_ROOT_PATHS = new Set(['/', '/app', '/app/chat']);

/**
 * Wires up the bits of native-shell behaviour that have no web equivalent.
 * A no-op on the web, and the plugin imports are dynamic so they stay out of
 * the web bundle entirely.
 *
 * Three things, all of which look broken if left to their defaults:
 *
 *  - **Android hardware back.** Capacitor's default is to exit the app when
 *    the WebView has no history to pop. Since rooms live in the URL, there is
 *    real history to walk first — without this, back from inside a room quits
 *    the app instead of returning to the room list.
 *  - **Status bar.** Left alone it renders dark-on-dark against the app's
 *    theme colour.
 *  - **Splash screen.** Hidden once React has mounted rather than on a fixed
 *    timer, so the user never sees a blank WebView between the two.
 */
export function useNativeShell() {
  useEffect(() => {
    if (!isNativeBuild) return;

    let removeBackButton: (() => void) | undefined;
    let cancelled = false;

    (async () => {
      const [{ App }, { StatusBar, Style }, { SplashScreen }] = await Promise.all([
        import('@capacitor/app'),
        import('@capacitor/status-bar'),
        import('@capacitor/splash-screen'),
      ]);

      if (cancelled) return;

      // Match the status bar to the app's theme colour instead of the
      // platform default. Android-only call, so failures are non-fatal.
      try {
        await StatusBar.setStyle({ style: Style.Dark });
        await StatusBar.setBackgroundColor({ color: '#128C7E' });
      } catch {
        /* iOS has no settable status-bar background — ignore. */
      }

      const handle = await App.addListener('backButton', () => {
        // Deliberately ignoring the event's `canGoBack`. It reflects the
        // native WebView's own navigation stack, which does not count
        // history.pushState — and pushState is how wouter moves between
        // routes. Measured on an emulator: inside /app/chat/<roomId> with
        // history.length === 2, canGoBack is still false, so trusting it
        // exits the app instead of returning to the room list.
        //
        // The document's own path is the reliable signal.
        const atRoot = APP_ROOT_PATHS.has(window.location.pathname);
        if (!atRoot && window.history.length > 1) {
          window.history.back();
        } else {
          // At the root, or deep-linked straight in with nothing to pop.
          App.exitApp();
        }
      });

      if (cancelled) {
        handle.remove();
        return;
      }
      removeBackButton = () => handle.remove();

      await SplashScreen.hide();
    })();

    return () => {
      cancelled = true;
      removeBackButton?.();
    };
  }, []);
}
